// ── 견적서 관리 상태·이펙트·핸들러 훅 ──────────────────────────────────────────
// QuoteManagement.jsx에서 분리 (300줄 초과 방지)
// 목록/폼/PDF/인쇄/도장 관련 모든 상태·이펙트·핸들러를 캡슐화

import { useEffect, useRef, useState } from 'react';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { apiClient } from '../../../api/apiClient';
import { useIsMobile } from './QuoteFormComponents';
import { toDateString } from '../../../utils/date';
import {
  fmtComma, parseComma, today, parseFreeItems, serializeFreeItems, emptyForm,
} from './constants';

// 모바일 바텀시트 드래그-투-디스미스 임계값 (px) — QuoteFormModal.jsx에서도 참조
export const SHEET_DISMISS_THRESHOLD = 110;

export function useQuoteManagement() {
  const isMobile = useIsMobile();

  // ── 목록 상태 ──────────────────────────────────────────────────────────────
  const [quotes,   setQuotes]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');

  // ── 폼 상태 ─────────────────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [editId,   setEditId]   = useState(null);
  const [form,     setForm]     = useState(emptyForm());
  const [freeRows, setFreeRows] = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [formErr,  setFormErr]  = useState('');

  // ── 연관 데이터 ─────────────────────────────────────────────────────────────
  const [salesPersons,     setSalesPersons]     = useState([]);
  const [promotionalItems, setPromotionalItems] = useState([]);

  // ── 도장 ───────────────────────────────────────────────────────────────────
  // stamp = "마지막으로 알던 도장 값". 로드 실패해도 절대 null로 덮지 않는다.
  // stampStatus: 'loading'(로드/재시도 중) | 'loaded'(GET 200 성공) | 'error'(재시도 소진)
  //   → 화면이 "도장 없음(업로드)"으로 보이는 건 'loaded' + stamp 없음일 때만.
  const [stamp,          setStamp]          = useState(null);
  const [stampStatus,    setStampStatus]    = useState('loading');
  const [stampUploading, setStampUploading] = useState(false);
  const stampInputRef = useRef();

  // ── 인쇄 ───────────────────────────────────────────────────────────────────
  const [printQuote, setPrintQuote] = useState(null);

  // ── PDF 저장 ────────────────────────────────────────────────────────────────
  const pdfRef               = useRef();
  const [pdfQuote, setPdfQuote]   = useState(null);
  const [pdfSaving, setPdfSaving] = useState(false);

  // ── 모바일 바텀시트 드래그 상태 + ref ──────────────────────────────────────
  const [sheetDragY, setSheetDragY] = useState(0);
  const sheetDragging   = useRef(false);   // 드래그 중 여부
  const sheetDragYRef   = useRef(0);        // 현재 드래그 Y (ref — 상태와 동기화)
  const sheetDragStartY = useRef(0);        // 드래그 시작 Y
  const sheetHandleRef  = useRef(null);     // 핸들 DOM 요소 ref

  // 로고 base64 — html2canvas가 외부 URL img를 캡처하지 못하는 경우 대비
  const [logoB64, setLogoB64] = useState(null);

  // ── 바텀시트 touch 리스너 ─────────────────────────────────────────────────
  useEffect(() => {
    if (!isMobile || !showForm) return;
    const el = sheetHandleRef.current;
    if (!el) return;

    const onStart = (e) => {
      sheetDragging.current   = true;
      sheetDragStartY.current = e.touches[0].clientY;
      sheetDragYRef.current   = 0;
      setSheetDragY(0);
    };
    const onMove = (e) => {
      if (!sheetDragging.current) return;
      e.preventDefault();       // pull-to-refresh 방지
      e.stopPropagation();
      const delta = e.touches[0].clientY - sheetDragStartY.current;
      if (delta > 0) { sheetDragYRef.current = delta; setSheetDragY(delta); }
    };
    const onEnd = () => {
      sheetDragging.current = false;
      if (sheetDragYRef.current >= SHEET_DISMISS_THRESHOLD) {
        setSheetDragY(0); setShowForm(false);
      } else {
        setSheetDragY(0);
      }
      sheetDragYRef.current = 0;
    };

    el.addEventListener('touchstart',  onStart, { passive: true });
    el.addEventListener('touchmove',   onMove,  { passive: false }); // non-passive 필수
    el.addEventListener('touchend',    onEnd,   { passive: true });
    el.addEventListener('touchcancel', onEnd,   { passive: true });
    return () => {
      el.removeEventListener('touchstart',  onStart);
      el.removeEventListener('touchmove',   onMove);
      el.removeEventListener('touchend',    onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [isMobile, showForm]);

  // ── 초기 데이터 로드 + 로고 base64 변환 ─────────────────────────────────────
  useEffect(() => {
    loadQuotes(); loadSalesPersons(); loadPromotionalItems();
    const cancelledRef = { current: false };
    loadStamp({ cancelledRef }); // 재시도 루프 — 언마운트 시 가드로 중단
    // 파인그룹 아이콘을 base64로 변환 — html2canvas PDF 캡처 시 안정적 렌더링
    fetch('/pine-icon-192.png')
      .then((r) => r.blob())
      .then((blob) => { const rd = new FileReader(); rd.onload = (e) => setLogoB64(e.target.result); rd.readAsDataURL(blob); })
      .catch(() => {});
    return () => { cancelledRef.current = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── API 호출 ─────────────────────────────────────────────────────────────────

  async function loadQuotes() {
    setLoading(true);
    const res = await apiClient.get('/api/admin/quotes');
    if (res.ok) setQuotes(res.data?.data || []);
    setLoading(false);
  }
  async function loadSalesPersons() {
    const res = await apiClient.get('/api/admin/quotes/sales-persons');
    if (res.ok) setSalesPersons(res.data?.data || []);
  }
  async function loadPromotionalItems() {
    const res = await apiClient.get('/api/admin/quotes/promotional-items');
    if (res.ok) setPromotionalItems(res.data?.data || []);
  }
  // 도장 로드: HTTP 200만 "확정"으로 보고, 5xx/네트워크 실패는 재시도(서버 재시작·배포 윈도우 자가 회복).
  // 실패해도 기존 stamp는 유지 → 단순 로드 실패로 화면에서 도장이 사라지지 않는다.
  // 인자 없이 호출하면(수동 "다시 불러오기") cancelledRef가 없어 항상 진행.
  async function loadStamp({ cancelledRef } = {}) {
    const MAX = 3;              // 총 3회 시도
    const BACKOFF = [400, 900]; // 시도 사이 대기(ms), 길이 = MAX-1
    for (let attempt = 0; attempt < MAX; attempt++) {
      if (cancelledRef?.current) return;
      if (attempt === 0) setStampStatus('loading');
      let res = null;
      try {
        res = await apiClient.get('/api/admin/quotes/stamp');
      } catch (_e) {
        res = null; // 네트워크 reject(서버 다운) → 재시도 대상
      }
      if (cancelledRef?.current) return;
      if (res && res.ok) {                          // 200 = 확정
        if (res.data?.data) setStamp(res.data.data); // 값 있으면 갱신, 없으면 기존 유지
        setStampStatus('loaded');
        return;
      }
      if (attempt < MAX - 1) {
        await new Promise((r) => setTimeout(r, BACKOFF[attempt]));
      }
    }
    if (!cancelledRef?.current) setStampStatus('error'); // 전부 실패: stamp 유지, 상태만 error
  }

  // ── 폼 핸들러 ────────────────────────────────────────────────────────────────

  async function openNew() {
    const res = await apiClient.get('/api/admin/quotes/next-number');
    setForm(emptyForm(res.ok ? (res.data?.data || '') : ''));
    setFreeRows([]); setEditId(null); setFormErr(''); setShowForm(true);
  }

  async function openEdit(q) {
    const res = await apiClient.get(`/api/admin/quotes/${encodeURIComponent(q.id)}`);
    if (!res.ok || !res.data?.data) {
      setFormErr('최신 견적서 정보를 불러오지 못했습니다. 새로고침 후 다시 시도해 주세요.');
      return;
    }
    const latest = res.data.data;
    setForm({
      quote_number:        latest.quote_number || '',
      issue_date:          latest.issue_date ? toDateString(latest.issue_date) : today(),
      requester_name:      latest.requester_name || '',
      recipient_name:      latest.recipient_name || '',
      business_number:     latest.business_number || '',
      product1_name:       latest.product1_name || '',
      product1_qty:        latest.product1_qty != null ? fmtComma(latest.product1_qty) : '',
      product1_unit_price: latest.product1_unit_price != null ? fmtComma(latest.product1_unit_price) : '',
      product2_name:       latest.product2_name || '',
      product2_qty:        latest.product2_qty != null ? fmtComma(latest.product2_qty) : '',
      vat_yn:              latest.vat_yn !== false,
      memo:                latest.memo || '',
    });
    setFreeRows(parseFreeItems(latest.free_items));
    setEditId(latest.id); setFormErr(''); setShowForm(true);
  }

  function setField(key, val) { setForm((prev) => ({ ...prev, [key]: val })); }

  async function handleSave() {
    if (!form.recipient_name.trim()) { setFormErr('공급받는 자(상호)를 입력해 주세요.'); return; }
    if (!form.product1_name)         { setFormErr('품목을 선택해 주세요.'); return; }
    setSaving(true); setFormErr('');
    const payload = {
      quote_number:        form.quote_number.trim(),
      issue_date:          form.issue_date,
      requester_name:      form.requester_name || null,
      recipient_name:      form.recipient_name.trim(),
      business_number:     form.business_number || null,
      product1_name:       form.product1_name || null,
      product1_qty:        parseComma(form.product1_qty),
      product1_unit_price: parseComma(form.product1_unit_price),
      product2_name:       form.product2_name || null,
      product2_qty:        parseComma(form.product2_qty),
      vat_yn:              form.vat_yn,
      free_items:          serializeFreeItems(freeRows),
      memo:                form.memo || null,
    };
    const res = editId
      ? await apiClient.put(`/api/admin/quotes/${editId}`, payload)
      : await apiClient.post('/api/admin/quotes', payload);
    setSaving(false);
    if (!res.ok) { setFormErr(res.data?.message || '저장 중 오류가 발생했습니다.'); return; }
    setShowForm(false); loadQuotes();
  }

  async function handleDelete(id) {
    if (!window.confirm('이 견적서를 삭제하시겠습니까?')) return;
    await apiClient.del(`/api/admin/quotes/${id}`); loadQuotes();
  }

  async function handleStampUpload(e) {
    const file = e.target.files?.[0]; if (!file) return;
    setStampUploading(true);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const b64 = ev.target.result;
      const res = await apiClient.post('/api/admin/quotes/stamp', { image: b64 });
      if (res.ok) { setStamp(b64); setStampStatus('loaded'); } // 업로드 성공 → error 배너 잔존 방지
      setStampUploading(false);
    };
    reader.readAsDataURL(file);
  }

  // 브라우저 인쇄 — PrintPortal에 quote 주입 후 window.print()
  // 항상 최신 도장(stamp) 을 fresh fetch 하여 race(첫 mount 직후 인쇄 시 stamp=null) 방지.
  async function handlePrint(q) {
    const stampRes = await apiClient.get('/api/admin/quotes/stamp');
    if (stampRes.ok && stampRes.data?.data) setStamp(stampRes.data.data);
    setPrintQuote(q);
    requestAnimationFrame(() => requestAnimationFrame(() => {
      window.print();
      window.addEventListener('afterprint', () => setPrintQuote(null), { once: true });
    }));
  }

  // PDF 저장 (모바일: navigator.share / 데스크탑: 직접 다운로드)
  // 항상 최신 도장(stamp) 을 fresh fetch — race 방지.
  async function handleSavePdf(q) {
    const stampRes = await apiClient.get('/api/admin/quotes/stamp');
    if (stampRes.ok && stampRes.data?.data) setStamp(stampRes.data.data);
    setPdfQuote(q); setPdfSaving(true);
    try {
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const el = pdfRef.current;
      if (!el) throw new Error('PDF 렌더 엘리먼트 없음');
      const canvas  = await html2canvas(el, { scale: 2, useCORS: true, allowTaint: true, logging: false, backgroundColor: '#ffffff' });
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const pdf     = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pageW   = pdf.internal.pageSize.getWidth();
      const pageH   = pdf.internal.pageSize.getHeight();
      pdf.addImage(imgData, 'JPEG', 0, 0, pageW, Math.min(pageW * (canvas.height / canvas.width), pageH));
      const fileName = `${q.recipient_name}_견적서.PDF`;
      if (isMobile && navigator.canShare) {
        const blob = pdf.output('blob');
        const file = new File([blob], fileName, { type: 'application/pdf' });
        if (navigator.canShare({ files: [file] })) { await navigator.share({ files: [file] }); return; }
      }
      pdf.save(fileName);
    } catch (err) {
      if (err?.name !== 'AbortError') { console.error('PDF 저장 오류:', err); alert('PDF 저장 중 오류가 발생했습니다.'); }
    } finally {
      setPdfSaving(false); setPdfQuote(null);
    }
  }

  // ── 파생 값 ──────────────────────────────────────────────────────────────────

  const filtered = quotes.filter((q) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (q.quote_number||'').toLowerCase().includes(s)
      || (q.recipient_name||'').toLowerCase().includes(s)
      || (q.requester_name||'').toLowerCase().includes(s)
      || (q.product1_name||'').toLowerCase().includes(s);
  });

  function calcTotal(q) {
    const s = (q.product1_qty||0) * (q.product1_unit_price||0);
    return (s + (q.vat_yn ? Math.round(s * 0.1) : 0)).toLocaleString('ko-KR');
  }

  return {
    isMobile,
    // 목록
    filtered, loading, search, setSearch, calcTotal,
    // 폼
    showForm, setShowForm, editId, form, freeRows, setFreeRows,
    saving, formErr, salesPersons, promotionalItems,
    // 도장
    stamp, stampStatus, stampUploading, stampInputRef, reloadStamp: loadStamp,
    // 인쇄
    printQuote, logoB64,
    // PDF
    pdfRef, pdfQuote, pdfSaving,
    // 바텀시트 드래그
    sheetDragY, sheetDragging, sheetHandleRef,
    // 핸들러
    openNew, openEdit, setField,
    handleSave, handleDelete, handleStampUpload,
    handlePrint, handleSavePdf,
  };
}
